import React, { useEffect, useState, useCallback } from "react";
import { api, ConversationSummary } from "../api";

interface Props {
  selectedId?: string;
  refreshKey: number;                         // increment to force re-fetch
  onSelect: (id: string) => void;
  onNew: () => void;
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1)  return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function ConversationSidebar({ selectedId, refreshKey, onSelect, onNew }: Props) {
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [deleting, setDeleting] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await api.getConversations();
      setConversations(data);
    } catch {
      // silently ignore — sidebar is non-critical
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const handleDelete = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setDeleting(id);
    try {
      await api.deleteConversation(id);
      setConversations(prev => prev.filter(c => c.id !== id));
      // If we deleted the currently selected conversation, notify parent
      if (id === selectedId) onNew();
    } catch {
      // ignore
    } finally {
      setDeleting(null);
    }
  };

  return (
    <aside className="sidebar">
      <div className="sidebar-header">
        <span className="sidebar-title">Conversations</span>
        <button className="new-chat-btn" onClick={onNew} title="Start new chat">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          New chat
        </button>
      </div>

      <div className="sidebar-list">
        {conversations.length === 0 && (
          <div className="sidebar-empty">No conversations yet. Ask your first question!</div>
        )}
        {conversations.map(conv => (
          <div
            key={conv.id}
            className={`sidebar-item ${conv.id === selectedId ? "active" : ""}`}
            onClick={() => onSelect(conv.id)}
          >
            <div className="sidebar-item-content">
              <span className="sidebar-item-title">{conv.title}</span>
              <span className="sidebar-item-time">{timeAgo(conv.updated_at)}</span>
            </div>
            <button
              className="sidebar-delete-btn"
              onClick={(e) => handleDelete(e, conv.id)}
              disabled={deleting === conv.id}
              title="Delete conversation"
            >
              {deleting === conv.id ? "…" : (
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
                  <path d="M10 11v6M14 11v6" />
                </svg>
              )}
            </button>
          </div>
        ))}
      </div>
    </aside>
  );
}
